import React, { useEffect, useState } from 'react';
import API from './api';
import { MapPin } from 'lucide-react';
import { FaBookOpen } from 'react-icons/fa';
import StudentDetailsModal from './StudentDetailsModal';
import { MapModal } from './MapModal';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Ikonica za marker (ista kao u AdminVisitsToday)
const teacherIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Jednostavan modal za single-location mapu (za G-sign), isti princip kao u AdminVisitsToday
function SimpleMapModal({ open, onClose, position, label }) {
  if (!open || !position?.lat || !position?.lon) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative p-4">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="text-sm mb-2">{label}</h3>
        <div className="h-80 w-full">
          <MapContainer
            center={[position.lat, position.lon]}
            zoom={15}
            scrollWheelZoom={false}
            className="h-full w-full rounded"
          >
            <TileLayer
              attribution="© OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[position.lat, position.lon]} icon={teacherIcon}>
              <Popup>{label}</Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

// helpers
const pad2 = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseYMDLocal = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function lastNDaysRange(n, base = new Date()) {
  const baseDate = typeof base === 'string' ? parseYMDLocal(base) : base;
  const end = startOfLocalDay(baseDate);
  const start = new Date(end);
  start.setDate(start.getDate() - n);
  return { from: fmt(start), to: fmt(end) };
}

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const dt = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  dt.setMinutes(dt.getMinutes() + (mins || 0));
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function groupByDate(visits) {
  return visits.reduce((acc, v) => {
    const date = v.scheduled_at.slice(0, 10);
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
    return acc;
  }, {});
}

function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

// Visit time + type + program_type (PI/EI) — uskladjeno sa AdminVisitsToday
function VisitTimeType({ scheduled_at, type, event_end_time, program_type }) {
  const from = scheduled_at?.slice(11, 16) || '';
  const to =
    (event_end_time && String(event_end_time).slice(0, 5)) ||
    (from ? addMinutesHHMM(from, 45) : '');

  const badgeColor =
    type === 'event'
      ? 'bg-yellow-100 text-orange-800'
      : 'bg-green-100 text-green-800';
  const typeLabel = type === 'event' ? 'Event' : 'Standard';

  const prog =
    program_type === 'PI'
      ? {
          label: 'PI',
          cls: 'bg-orange-100 text-orange-800 border border-orange-300',
        }
      : program_type === 'EI'
      ? {
          label: 'EI',
          cls: 'bg-blue-100 text-blue-800 border border-blue-300',
        }
      : null;

  return (
    <div className="flex flex-col items-start">
      <span className="font-mono text-base">
        {from && to ? `${from}–${to}` : '-'}
      </span>
      <div className="mt-1 flex items-center gap-1">
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badgeColor}`}>
          {typeLabel}
        </span>
        {prog && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${prog.cls}`}>
            {prog.label}
          </span>
        )}
      </div>
    </div>
  );
}

// CSV export
function exportToCSV(visits) {
  if (!visits.length) return;
  const headers = Object.keys(visits[0]);
  const csvRows = [
    headers.join(','),
    ...visits.map(v =>
      headers.map(h => `"${(v[h] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'visits.csv';
  a.click();
  window.URL.revokeObjectURL(url);
}

function compareDate(a, b) {
  return a.localeCompare(b);
}

function AdminVisits() {
  const [visits, setVisits] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);

  const [filters, setFilters] = useState(() => {
    const { from, to } = lastNDaysRange(15);
    return {
      dateFrom: from,
      dateTo: to,
      teacherId: '',
      studentId: '',
      status: '',
      search: '',
    };
  });

  const [serverDateStr, setServerDateStr] = useState(null);

  const [studentDetailsModal, setStudentDetailsModal] = useState({ open: false, student: null });
  const [mapModal, setMapModal] = useState({ open: false, student: null, teacher: null });

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  const [gsignLog, setGsignLog] = useState({});
  const [gsignMapModal, setGsignMapModal] = useState({ open: false, position: null, date: null });

  const round2 = (num) => Math.round(Number(num) * 100) / 100;

  const openStudentModal = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  const openMapModalFunc = async (studentId, teacherId) => {
    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id: studentId, teacher_id: teacherId },
      });
      setMapModal({
        open: true,
        student: res.data.student || null,
        teacher: res.data.teacher || null,
      });
    } catch {
      alert('Failed to fetch map data');
    }
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  useEffect(() => {
    API.get('/server-time')
      .then(res => {
        const srv = res.data?.serverTime?.slice(0, 10);
        setServerDateStr(srv || null);
        if (srv) {
          setFilters(f => {
            const { from, to } = lastNDaysRange(15, srv);
            return {
              ...f,
              dateFrom: f.dateFrom || from,
              dateTo: f.dateTo || to,
            };
          });
        }
      })
      .catch(() => setServerDateStr(null));

    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFilters = async () => {
    const t = await API.get('/teachers');
    setTeachers(t.data || []);
    const s = await API.get('/students/all');
    const sorted = (s.data || []).slice().sort((a, b) => {
      const la = (a.last_name || '').localeCompare(b.last_name || '', 'en', { sensitivity: 'base' });
      return la !== 0
        ? la
        : (a.first_name || '').localeCompare(b.first_name || '', 'en', { sensitivity: 'base' });
    });
    setStudents(sorted);
  };

  const fetchVisits = async () => {
    if (!filters.dateFrom && !filters.dateTo) return;

    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.teacherId) params.teacher_id = filters.teacherId;
    if (filters.studentId) params.student_id = filters.studentId;
    if (filters.status) params.status = filters.status;

    const res = await API.get('/admin/visits', { params });
    let data = res.data || [];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      data = data.filter(
        v =>
          (v.student_name && v.student_name.toLowerCase().includes(term)) ||
          (v.teacher_name && v.teacher_name.toLowerCase().includes(term))
      );
    }

    // ostavi redosled kakav vrati BE ili dodaj sort po potrebi
    setVisits(data);

    if (data.length) {
      const ids = data.map(v => v.visit_id).filter(Boolean).join(',');
      if (ids) {
        const logs = await API.get('/visit-gsign-log', { params: { visit_ids: ids } });
        setGsignLog(logs.data || {});
      } else {
        setGsignLog({});
      }
    } else {
      setGsignLog({});
    }
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  function getCurrentWeekRange(baseDateStr) {
    const base = parseYMDLocal(baseDateStr);
    const day = base.getDay() === 0 ? 7 : base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { monday: fmt(monday), sunday: fmt(sunday) };
  }

  const setQuickFilter = (type) => {
    if (!serverDateStr) return;
    if (type === 'today') {
      setFilters(f => ({ ...f, dateFrom: serverDateStr, dateTo: serverDateStr }));
    } else if (type === 'week') {
      const { monday, sunday } = getCurrentWeekRange(serverDateStr);
      setFilters(f => ({ ...f, dateFrom: monday, dateTo: sunday }));
    } else if (type === 'future') {
      setFilters(f => ({ ...f, dateFrom: serverDateStr, dateTo: '' }));
    }
  };

  useEffect(() => {
    if (!filters.dateFrom && !filters.dateTo) return;
    fetchVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateFrom, filters.dateTo, filters.teacherId, filters.studentId, filters.status, filters.search]);

  const grouped = groupByDate(visits);

  return (
    <div className="w-full px-1 sm:px-1">
      <h2 className="text-lg font-bold mb-4">All Scheduled Visits</h2>

      {/* FILTER CARD */}
      <div className="w-full bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 flex flex-col gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date From</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date To</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Teacher</label>
            <select
              name="teacherId"
              value={filters.teacherId}
              onChange={handleChange}
              className="border p-2 rounded"
            >
              <option value="">All Teachers</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Student</label>
            <select
              name="studentId"
              value={filters.studentId}
              onChange={handleChange}
              className="border p-2 rounded"
            >
              <option value="">All Students</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>
                  {(s.last_name || '').trim()}, {(s.first_name || '').trim()}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleChange}
              className="border p-2 rounded"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="complete">Complete</option>
              <option value="canceled">Canceled</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              placeholder="Search student/teacher"
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>
        </div>

        <button
          onClick={fetchVisits}
          className="mt-2 bg-blue-600 text-white px-4 py-2 rounded w-full sm:w-auto"
        >
          Apply Filters
        </button>
      </div>

      {/* Fast filters + CSV */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <button onClick={() => setQuickFilter('today')} className="text-xs bg-gray-200 rounded px-2 py-1">Today</button>
        <button onClick={() => setQuickFilter('week')} className="text-xs bg-gray-200 rounded px-2 py-1">This Week</button>
        <button onClick={() => setQuickFilter('future')} className="text-xs bg-gray-200 rounded px-2 py-1">All Future</button>
        <button onClick={() => exportToCSV(visits)} className="text-xs bg-green-200 rounded px-2 py-1 ml-auto">Export CSV</button>
      </div>

      {/* LIST */}
      {Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">No visits found for the selected criteria.</div>
      ) : !serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">Loading server time...</div>
      ) : (
        Object.entries(grouped).map(([date, group]) => (
          <div key={date} className="mb-6 shadow rounded-lg bg-white border">
            <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
              <span>{date}</span>
              <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                {group.length} visits
              </span>
            </div>

            {/* DESKTOP */}
            <div className="overflow-x-auto hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1">Student</th>
                    <th className="px-2 py-1">Teacher</th>
                    <th className="px-2 py-1">Status</th>
                    <th className="px-2 py-1">Reassigned</th>
                    <th className="px-2 py-1">G-sign</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map(v => {
                    const visitDateStr = v.scheduled_at.slice(0, 10);
                    let rowClass = 'hover:bg-gray-50 cursor-pointer';
                    if (compareDate(visitDateStr, serverDateStr) < 0) rowClass += ' opacity-60 bg-gray-100';
                    else if (compareDate(visitDateStr, serverDateStr) > 0) rowClass += ' opacity-90 bg-yellow-50';
                    else rowClass += ' bg-green-50';

                    const gsign = gsignLog[v.visit_id];
                    const hasGsign = gsign && gsign.lat != null && gsign.lon != null;
                    const gLat2 = hasGsign ? round2(gsign.lat) : null;
                    const gLon2 = hasGsign ? round2(gsign.lon) : null;
                    const checkinLat2 = v.checkin_lat != null ? round2(v.checkin_lat) : null;
                    const checkinLon2 = v.checkin_lon != null ? round2(v.checkin_lon) : null;
                    const checkoutLat2 = v.checkout_lat != null ? round2(v.checkout_lat) : null;
                    const checkoutLon2 = v.checkout_lon != null ? round2(v.checkout_lon) : null;

                    let gColor = 'text-gray-400';
                    if (hasGsign) {
                      const checkinMatch =
                        checkinLat2 != null &&
                        checkinLon2 != null &&
                        gLat2 === checkinLat2 &&
                        gLon2 === checkinLon2;
                      const checkoutMatch =
                        checkoutLat2 != null &&
                        checkoutLon2 != null &&
                        gLat2 === checkoutLat2 &&
                        gLon2 === checkoutLon2;
                      if (checkinMatch || checkoutMatch) gColor = 'text-green-700';
                      else gColor = 'text-red-600';
                    }

                    return (
                      <tr key={v.visit_id} className={rowClass}>
                        <td className="px-2 py-1">
                          <VisitTimeType
                            scheduled_at={v.scheduled_at}
                            type={v.type}
                            event_end_time={v.event_end_time}
                            program_type={v.program_type}
                          />
                        </td>
                        <td className="px-2 py-1 flex items-center gap-2">
                          <span
                            onClick={() => openStudentModal(v.student_id)}
                            className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                            title="Student Details"
                          >
                            {v.student_name}
                          </span>
                          <button
                            onClick={() => openMapModalFunc(v.student_id, v.teacher_id)}
                            className="ml-2 text-gray-400 hover:text-blue-600"
                            title="Show on Map"
                          >
                            <MapPin size={18} />
                          </button>
                          <button
                            onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                            className="ml-2 text-purple-700 hover:text-purple-900"
                            title="Show Notes History"
                          >
                            <FaBookOpen size={18} />
                          </button>
                        </td>
                        <td className="px-2 py-1">
                          <div>{v.teacher_name}</div>
                          {v.teacher_id !== v.original_teacher_id && v.original_teacher_name && (
                            <div className="text-xs text-gray-400 mt-1">
                              Default: {v.original_teacher_name}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1 capitalize">
                          <StatusBadge status={v.status}>{v.status}</StatusBadge>
                          {v.status === 'canceled' && v.notes && (
                            <div className="text-xs text-purple-700 mt-1 italic">
                              {v.notes}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-xs text-gray-400">
                          {v.is_reassigned ? (
                            <StatusBadge status={v.reassigned_status}>
                              {v.reassigned_status}
                            </StatusBadge>
                          ) : (
                            'Default Teacher'
                          )}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {v.g_sign_confirmed ? (
                            <div className="flex flex-col items-center">
                              <div className="flex flex-row items-center gap-1">
                                <span className="text-green-600 text-md">✅</span>
                                {hasGsign && (
                                  <button
                                    title="Show confirmation location"
                                    className={gColor}
                                    onClick={e => {
                                      e.stopPropagation();
                                      setGsignMapModal({
                                        open: true,
                                        position: { lat: Number(gsign.lat), lon: Number(gsign.lon) },
                                        date: gsign.confirmed_at,
                                      });
                                    }}
                                  >
                                    <MapPin size={16} />
                                  </button>
                                )}
                              </div>
                              {gsign && (
                                <span className={`text-xs mt-0.5 ${gColor}`}>
                                  {gsign.confirmed_at?.slice(0, 16).replace('T', ' ')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-red-600 text-md">❌</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE */}
            <div className="sm:hidden flex flex-col gap-2 p-2">
              {group.map(v => {
                const visitDateStr = v.scheduled_at.slice(0, 10);
                let rowClass = 'rounded-2xl border shadow p-3 mb-1 ';
                if (compareDate(visitDateStr, serverDateStr) < 0) rowClass += ' opacity-60 bg-gray-100';
                else if (compareDate(visitDateStr, serverDateStr) > 0) rowClass += ' opacity-90 bg-yellow-50';
                else rowClass += ' bg-green-50';

                const gsign = gsignLog[v.visit_id];
                const hasGsign = gsign && gsign.lat != null && gsign.lon != null;
                const gLat2 = hasGsign ? round2(gsign.lat) : null;
                const gLon2 = hasGsign ? round2(gsign.lon) : null;
                const checkinLat2 = v.checkin_lat != null ? round2(v.checkin_lat) : null;
                const checkinLon2 = v.checkin_lon != null ? round2(v.checkin_lon) : null;
                const checkoutLat2 = v.checkout_lat != null ? round2(v.checkout_lat) : null;
                const checkoutLon2 = v.checkout_lon != null ? round2(v.checkout_lon) : null;

                let gColor = 'text-gray-400';
                if (hasGsign) {
                  const checkinMatch =
                    checkinLat2 != null &&
                    checkinLon2 != null &&
                    gLat2 === checkinLat2 &&
                    gLon2 === checkinLon2;
                  const checkoutMatch =
                    checkoutLat2 != null &&
                    checkoutLon2 != null &&
                    gLat2 === checkoutLat2 &&
                    gLon2 === checkoutLon2;
                  if (checkinMatch || checkoutMatch) gColor = 'text-green-700';
                  else gColor = 'text-red-600';
                }

                return (
                  <div key={v.visit_id} className={rowClass}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 flex items-center gap-2">
                        <span
                          onClick={() => openStudentModal(v.student_id)}
                          className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                        >
                          {v.student_name}
                        </span>
                        <button
                          onClick={() => openMapModalFunc(v.student_id, v.teacher_id)}
                          className="ml-1 text-gray-400 hover:text-blue-600"
                          title="Show on Map"
                        >
                          <MapPin size={16} />
                        </button>
                        <button
                          onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                          className="ml-1 text-purple-700 hover:text-purple-900"
                          title="Show Notes History"
                        >
                          <FaBookOpen size={16} />
                        </button>
                      </div>
                      <div className="flex-1 text-right">
                        <VisitTimeType
                          scheduled_at={v.scheduled_at}
                          type={v.type}
                          event_end_time={v.event_end_time}
                          program_type={v.program_type}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center mt-2">
                      <StatusBadge status={v.status}>{v.status}</StatusBadge>
                      {v.status === 'canceled' && v.notes && (
                        <span className="text-xs text-purple-700 italic">{v.notes}</span>
                      )}
                      {v.teacher_id !== v.original_teacher_id && v.reassigned_status && (
                        <span
                          className={`text-xs ${
                            v.reassigned_status === 'rejected'
                              ? 'text-red-700'
                              : 'text-orange-700'
                          }`}
                        >
                          Substitute: {v.reassigned_status}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div>
                        <span className="block text-xs text-gray-600">Teacher</span>
                        <span>{v.teacher_name}</span>
                        {v.teacher_id !== v.original_teacher_id && v.original_teacher_name && (
                          <div className="text-xs text-gray-400 mt-1">
                            Default: {v.original_teacher_name}
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="block text-xs text-gray-600">G-sign</span>
                        {v.g_sign_confirmed ? (
                          <div className="flex flex-row items-center gap-1">
                            <span className="text-green-600 text-md">✅</span>
                            {hasGsign && (
                              <button
                                title="Show confirmation location"
                                className={gColor}
                                onClick={e => {
                                  e.stopPropagation();
                                  setGsignMapModal({
                                    open: true,
                                    position: { lat: Number(gsign.lat), lon: Number(gsign.lon) },
                                    date: gsign.confirmed_at,
                                  });
                                }}
                              >
                                <MapPin size={16} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-red-600 text-md">❌</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Modals */}
      <StudentDetailsModal
        open={studentDetailsModal.open}
        onClose={() => setStudentDetailsModal({ open: false, student: null })}
        student={studentDetailsModal.student}
      />

      {/* standard student+teacher map (isti MapModal kao MyVisits/MyVisitsToday) */}
      <MapModal
        open={mapModal.open}
        onClose={() => setMapModal({ open: false, student: null, teacher: null })}
        student={mapModal.student}
        teacher={mapModal.teacher}
      />

      {/* G-sign mapa (isti UX kao AdminVisitsToday) */}
      <SimpleMapModal
        open={gsignMapModal.open}
        onClose={() => setGsignMapModal({ open: false, position: null, date: null })}
        position={gsignMapModal.position}
        label={`Parent confirmed location${
          gsignMapModal.date
            ? ` at ${gsignMapModal.date.slice(0, 16).replace('T', ' ')}`
            : ''
        }`}
      />

      {/* Notes history */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">
                Notes History: {notesHistoryStudent}
              </h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>✕</button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at.slice(0, 16).replace('T', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {[
                        { key: 'notes_cognitive', label: 'Cognitive' },
                        { key: 'notes_language', label: 'Language' },
                        { key: 'notes_gross_motor', label: 'Gross Motor' },
                        { key: 'notes_fine_motor', label: 'Fine Motor' },
                        { key: 'notes_social', label: 'Social' },
                        { key: 'notes_self_help', label: 'Self Help' },
                      ].map(dom =>
                        entry[dom.key] && entry[dom.key].trim() !== '' ? (
                          <div key={dom.key}>
                            <span className="font-semibold">{dom.label}:</span>{' '}
                            <span>{entry[dom.key]}</span>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisits;








